import { ShopExtended, ShopBanner, ShopWidget, ShopPage, ShopPolicy, ShopGallery } from '../../domain/types';
import { COLLECTIONS } from '../../domain/collections';
import { db } from '../../infrastructure/firebaseConfig';
import { logger } from '../../infrastructure/logger';
import { CacheInvalidator } from '../../utils/cacheInvalidation';
import {
  collection,
  getDocs,
  doc as firestoreDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  writeBatch
} from "firebase/firestore";
import { shopQueries } from './shopQueries';

export class ShopMutations {
  private shopsColl = COLLECTIONS.SHOPS;
  private bannersColl = 'shopBanners';
  private widgetsColl = 'shopWidgets';
  private pagesColl = 'shopPages';
  private policiesColl = 'shopPolicies';
  private galleriesColl = 'shopGalleries';

  async createShop(shop: Omit<ShopExtended, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, this.shopsColl), {
      ...shop,
      banners: [],
      widgets: [],
      pages: [],
      policies: [],
      galleries: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    CacheInvalidator.invalidateShop(docRef.id);
    return docRef.id;
  }

  async updateShop(shopId: string, updates: Partial<ShopExtended>): Promise<void> {
    const docRef = firestoreDoc(db, this.shopsColl, shopId);
    await updateDoc(docRef, { ...updates, updatedAt: new Date().toISOString() });
    CacheInvalidator.invalidateShop(shopId);
  }

  async updateShopTheme(shopId: string, theme: ShopExtended['theme']): Promise<void> {
    await this.updateShop(shopId, { theme });
  }

  async updateShopLayout(shopId: string, layout: ShopExtended['layout']): Promise<void> {
    await this.updateShop(shopId, { layout });
  }

  async updateShopSettings(shopId: string, settings: Partial<ShopExtended['settings']>): Promise<void> {
    const shop = await shopQueries.getShopById(shopId);
    if (shop) {
      await this.updateShop(shopId, {
        settings: { ...shop.settings, ...settings }
      });
    }
  }

  async updateShopSEO(shopId: string, seo: ShopExtended['seo']): Promise<void> {
    await this.updateShop(shopId, { seo });
  }

  async updateShopSocialLinks(shopId: string, socialLinks: ShopExtended['socialLinks']): Promise<void> {
    await this.updateShop(shopId, { socialLinks });
  }

  async deleteShop(shopId: string): Promise<void> {
    const batch = writeBatch(db);
    batch.delete(firestoreDoc(db, this.shopsColl, shopId));
    const banners = await shopQueries.getBanners(shopId);
    banners.forEach(b => batch.delete(firestoreDoc(db, this.bannersColl, b.id)));
    const widgets = await shopQueries.getWidgets(shopId);
    widgets.forEach(w => batch.delete(firestoreDoc(db, this.widgetsColl, w.id)));
    const pages = await shopQueries.getPages(shopId);
    pages.forEach(p => batch.delete(firestoreDoc(db, this.pagesColl, p.id)));
    const policies = await shopQueries.getPolicies(shopId);
    policies.forEach(p => batch.delete(firestoreDoc(db, this.policiesColl, p.id)));
    const galleries = await shopQueries.getGalleries(shopId);
    galleries.forEach(g => batch.delete(firestoreDoc(db, this.galleriesColl, g.id)));
    await batch.commit();
    CacheInvalidator.invalidateShop(shopId);
  }

  async approveShop(shopId: string, approvedBy: string): Promise<void> {
    await updateDoc(firestoreDoc(db, this.shopsColl, shopId), {
      status: 'active',
      approvedBy,
      approvedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    CacheInvalidator.invalidateShop(shopId);
  }

  async rejectShop(shopId: string, reason: string): Promise<void> {
    await updateDoc(firestoreDoc(db, this.shopsColl, shopId), {
      status: 'rejected',
      rejectionReason: reason,
      updatedAt: new Date().toISOString()
    });
    CacheInvalidator.invalidateShop(shopId);
  }

  async suspendShop(shopId: string, reason?: string): Promise<void> {
    await updateDoc(firestoreDoc(db, this.shopsColl, shopId), {
      status: 'suspended',
      suspensionReason: reason,
      updatedAt: new Date().toISOString()
    });
    CacheInvalidator.invalidateShop(shopId);
  }

  async featureShop(shopId: string, days: number = 7): Promise<void> {
    const featuredUntil = new Date();
    featuredUntil.setDate(featuredUntil.getDate() + days);
    await updateDoc(firestoreDoc(db, this.shopsColl, shopId), {
      featured: true,
      featuredUntil: featuredUntil.toISOString(),
      updatedAt: new Date().toISOString()
    });
    CacheInvalidator.invalidateShop(shopId);
  }

  async unfeatureShop(shopId: string): Promise<void> {
    await updateDoc(firestoreDoc(db, this.shopsColl, shopId), {
      featured: false,
      featuredUntil: null,
      updatedAt: new Date().toISOString()
    });
    CacheInvalidator.invalidateShop(shopId);
  }

  async addBanner(shopId: string, banner: Omit<ShopBanner, 'id'>): Promise<string> {
    const docRef = await addDoc(collection(db, this.bannersColl), {
      ...banner,
      shopId,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  }

  async updateBanner(bannerId: string, updates: Partial<ShopBanner>): Promise<void> {
    await updateDoc(firestoreDoc(db, this.bannersColl, bannerId), updates);
  }

  async deleteBanner(bannerId: string): Promise<void> {
    await deleteDoc(firestoreDoc(db, this.bannersColl, bannerId));
  }

  async addWidget(shopId: string, widget: Omit<ShopWidget, 'id'>): Promise<string> {
    const docRef = await addDoc(collection(db, this.widgetsColl), {
      ...widget,
      shopId,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  }

  async updateWidget(widgetId: string, updates: Partial<ShopWidget>): Promise<void> {
    await updateDoc(firestoreDoc(db, this.widgetsColl, widgetId), updates);
  }

  async deleteWidget(widgetId: string): Promise<void> {
    await deleteDoc(firestoreDoc(db, this.widgetsColl, widgetId));
  }

  async addPage(shopId: string, page: Omit<ShopPage, 'id'>): Promise<string> {
    const docRef = await addDoc(collection(db, this.pagesColl), {
      ...page,
      shopId,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  }

  async updatePage(pageId: string, updates: Partial<ShopPage>): Promise<void> {
    await updateDoc(firestoreDoc(db, this.pagesColl, pageId), updates);
  }

  async deletePage(pageId: string): Promise<void> {
    await deleteDoc(firestoreDoc(db, this.pagesColl, pageId));
  }

  async addPolicy(shopId: string, policy: Omit<ShopPolicy, 'id'>): Promise<string> {
    const docRef = await addDoc(collection(db, this.policiesColl), {
      ...policy,
      shopId,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  }

  async updatePolicy(policyId: string, updates: Partial<ShopPolicy>): Promise<void> {
    await updateDoc(firestoreDoc(db, this.policiesColl, policyId), updates);
  }

  async deletePolicy(policyId: string): Promise<void> {
    await deleteDoc(firestoreDoc(db, this.policiesColl, policyId));
  }

  async addGallery(shopId: string, gallery: Omit<ShopGallery, 'id'>): Promise<string> {
    const docRef = await addDoc(collection(db, this.galleriesColl), {
      ...gallery,
      shopId,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  }

  async updateGallery(galleryId: string, updates: Partial<ShopGallery>): Promise<void> {
    await updateDoc(firestoreDoc(db, this.galleriesColl, galleryId), updates);
  }

  async deleteGallery(galleryId: string): Promise<void> {
    await deleteDoc(firestoreDoc(db, this.galleriesColl, galleryId));
  }
}

export const shopMutations = new ShopMutations();
